import { APP_COUNTRY } from 'config';
import { verifyContactsEndpoint } from 'pages/profile/profile.config';
import {
  makeSelectAuthApiConfig,
  makeSelectResourceLoadingState,
} from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import {
  idDocumentState,
  IdDocumentStateType,
} from 'services/identityDocument/indentityDocument.config';
import { AppState } from 'store/types';
import {
  getDocumentExpirationDaysEndpoint,
  UserStatus,
} from './dashboard.config';

const lowerExpirationDays: number = 1;
const upperExpirationDays: number = 7;

export function computeUserIdState(
  documentState: IdDocumentStateType,
  passwordState: number,
): UserStatus {
  if (
    passwordState >= lowerExpirationDays &&
    passwordState <= upperExpirationDays
  ) {
    return UserStatus.passwordExpiring;
  } else if (documentState === idDocumentState.expired) {
    return UserStatus.documentExpired;
  } else if (documentState === idDocumentState.expiringSoon) {
    return UserStatus.documentExpiring;
  } else if (documentState === idDocumentState.notSent) {
    return UserStatus.documentNotSent;
  } else if (documentState === idDocumentState.rejected) {
    return UserStatus.documentRejected;
  } else {
    return UserStatus.ok;
  }
}

export function selectGetDocumentExpirationDaysApiConfig(
  reduxState: AppState,
): RequestApiDataIfNecessaryPayload {
  const url = getDocumentExpirationDaysEndpoint;

  const requestConfig = makeSelectAuthApiConfig({
    url,
    method: 'GET',
  })(reduxState);

  return {
    resourceId: url,
    requestConfig,
    invalidateIfPresent: true,
  };
}

export const mapUserStatusToAlertBarData = (
  isProtocolValidated: boolean,
  userStatus: UserStatus,
  DocExpirationDays: number,
) => {
  const isFrance = APP_COUNTRY == 'fr';
  if (!isProtocolValidated && isFrance) {
    return {
      labels: [{ id: 'text.dashboard.alert.accountNotValidated' }],
      buttons: {
        desktop: 'lbl.recharge.verifyId.modal.button',
        mobile: 'lbl.recharge.verifyId.modal.button',
      },
    };
  }
  switch (userStatus) {
    case UserStatus.ok:
      return undefined;
    case UserStatus.documentExpired:
      return {
        labels: [{ id: 'text.dashboard.alert.documentExpired' }],
        buttons: { desktop: 'lbl.load' },
      };
    case UserStatus.documentExpiring:
      if (DocExpirationDays === 0) {
        return {
          labels: [
            { id: 'text.dashboard.alert.documentExpiring' },
            { id: 'text.alertBar.documentIsExpiringSameDay' },
          ],
          buttons: { desktop: 'lbl.load' },
        };
      } else {
        return {
          labels: [
            { id: 'text.dashboard.alert.documentExpiring' },
            {
              id: 'text.alertBar.documentIsExpiring',
              data: {
                word1: DocExpirationDays === 1 ? 'Manca' : 'Mancano',
                daysToExpiration: DocExpirationDays,
                word2: DocExpirationDays === 1 ? 'giorno' : 'giorni',
              },
            },
          ],
          buttons: { desktop: 'lbl.load' },
        };
      }
    case UserStatus.documentNotSent:
      if (DocExpirationDays === 0) {
        return {
          labels: [
            { id: 'text.dashboard.alert.documentNotSent' },
            { id: 'text.alertBar.documentNotSentSameDay' },
          ],
          buttons: { desktop: 'lbl.load' },
        };
      } else {
        return {
          labels: [
            { id: 'text.dashboard.alert.documentNotSent' },
            {
              id: 'text.alertBar.documentNotSent',
              data: {
                daysToExpiration: DocExpirationDays,
                word1:
                  DocExpirationDays === 1
                    ? getDayLabelForEachForEachCountry(dayTypeEnum.ONE)
                    : getDayLabelForEachForEachCountry(dayTypeEnum.MORE),
              },
            },
          ],
          buttons: { desktop: 'lbl.load' },
        };
      }
    case UserStatus.passwordExpiring:
      return {
        labels: [{ id: 'text.dashboard.alert.passwordExpiring' }],
        buttons: {
          desktop: 'lbl.profile.modify',
          mobile: 'lbl.profile.modify',
        },
        redirect: '/profile#modifyPassword',
      };
    case UserStatus.documentRejected:
      return {
        labels: [{ id: 'text.dashboard.alert.documentRejected' }],
        buttons: { desktop: 'lbl.load' },
      };
    default:
      return undefined;
  }
};

export const selectGetDocumentExpirationDaysLoadState =
  makeSelectResourceLoadingState(getDocumentExpirationDaysEndpoint);

export const selectGetVerifiedContactsLoadState =
  makeSelectResourceLoadingState(verifyContactsEndpoint);

export enum dayTypeEnum {
  'ONE' = 'ONE',
  'MORE' = 'MORE',
}

export const getDayLabelForEachForEachCountry = (type: dayTypeEnum) => {
  switch (APP_COUNTRY) {
    case 'it':
      return type === 'ONE' ? 'giorno' : 'giorni';
    case 'es':
      return type === 'ONE' ? 'día' : 'días';
    case 'pt':
      return type === 'ONE' ? 'dia' : 'dias';
    case 'fr':
      return type === 'ONE' ? 'jour' : 'jours';
    default:
      return type === 'ONE' ? 'giorno' : 'giorni';
  }
};
